// eslint-disable import/no-unresolved
// Use type-only import to avoid bundling Sentry statically
import type * as SentryTypes from '@sentry/react';

import coreInteractionMetrics from '@gumtree/web-bff/src/metrics/modules/core-interaction-metrics';
import logger from '../logger';

// Store Sentry instance after async load for later use
let SentryInstance: typeof SentryTypes | null = null;

const THIRD_PARTY_HOST_WHITELIST = ['localhost', '127.0.0.1'];

// Use generic types that match Sentry's structure
type StackFrame = {
    filename?: string;
    abs_path?: string;
    function?: string;
    lineno?: number;
    in_app?: boolean;
};

type SentryEvent = {
    exception?: {
        values?: Array<{
            stacktrace?: {
                frames?: StackFrame[];
            };
        }>;
    };
    message?: string;
};

const getFrames = (event: SentryEvent): StackFrame[] =>
    event.exception?.values?.[0]?.stacktrace?.frames || [];

const getErrorFrame = (frames: StackFrame[]): StackFrame | null =>
    frames.length ? frames[frames.length - 1] : null;

const makeStackCheckers = (predicate: (frame: StackFrame) => boolean) => ({
    causedBy: (frames: StackFrame[]) => {
        const errorFrame = getErrorFrame(frames);
        return Boolean(errorFrame && predicate(errorFrame));
    },
    onlyFrom: (frames: StackFrame[]) => frames.length > 0 && frames.every(predicate),
    involving: (frames: StackFrame[]) => frames.some(predicate),
});

/**
 * Check if a single frame is from node_modules
 */
const isFrameFromNodeModules = (frame: StackFrame): boolean => {
    if (!frame) {
        return false;
    }

    // Method 1: Check filename
    if (frame.filename?.includes('node_modules')) {
        return true;
    }

    // Method 2: Check absolute path
    if (frame.abs_path?.includes('node_modules')) {
        return true;
    }

    // Method 3: Check in_app flag (needs to be combined with other checks as it may not be accurate)
    // Note: in_app === false doesn't necessarily mean it's from node_modules, it could also be system code
    if (frame.in_app === false && frame.filename && !frame.filename.startsWith('webpack://')) {
        return true;
    }

    return false;
};

/**
 * Check if a single frame is from a third-party script (loaded from an external origin)
 */
const isFrameFromThirdPartyScript = (frame: StackFrame): boolean => {
    if (!frame) {
        return false;
    }

    const target = frame.filename || frame.abs_path;
    if (!target) {
        return false;
    }

    const isHttpUrl = /^https?:\/\//.test(target);

    if (isHttpUrl) {
        try {
            const url = new URL(target);
            const sameOrigin =
                typeof window !== 'undefined' && url.origin === window.location.origin;
            const whitelistedHost =
                typeof window !== 'undefined' &&
                (THIRD_PARTY_HOST_WHITELIST.includes(url.hostname) ||
                    url.hostname === window.location.hostname);

            if (!sameOrigin && !whitelistedHost) {
                return true;
            }
        } catch {
            // If URL parsing fails, treat it conservatively as third-party
            return true;
        }
    }

    // Fallback heuristic: frames marked as out-of-app and not webpack in-app paths
    if (frame.in_app === false && frame.filename && !frame.filename.startsWith('webpack://')) {
        return true;
    }

    return false;
};

const nodeModulesCheck = makeStackCheckers(isFrameFromNodeModules);
const thirdPartyScriptCheck = makeStackCheckers(isFrameFromThirdPartyScript);

/**
 * Get detailed information about the error source
 */
const getErrorSourceInfo = (event: SentryEvent) => {
    const frames = getFrames(event);
    if (!frames.length) {
        return {
            causedByNodeModules: false,
            onlyFromNodeModules: false,
            involvingNodeModules: false,
            causedByThirdPartyScript: false,
            onlyFromThirdPartyScript: false,
            involvingThirdPartyScript: false,
            errorFrame: null,
            nodeModulesFrames: [],
            thirdPartyFrames: [],
            totalFrames: 0,
            nodeModulesFrameCount: 0,
            thirdPartyFrameCount: 0,
        };
    }

    const errorFrame = getErrorFrame(frames);
    const nodeModulesFrames = frames.filter(isFrameFromNodeModules);
    const thirdPartyFrames = frames.filter(isFrameFromThirdPartyScript);

    return {
        causedByNodeModules: nodeModulesCheck.causedBy(frames),
        onlyFromNodeModules: nodeModulesCheck.onlyFrom(frames),
        involvingNodeModules: nodeModulesCheck.involving(frames),
        causedByThirdPartyScript: thirdPartyScriptCheck.causedBy(frames),
        onlyFromThirdPartyScript: thirdPartyScriptCheck.onlyFrom(frames),
        involvingThirdPartyScript: thirdPartyScriptCheck.involving(frames),
        errorFrame: {
            filename: errorFrame?.filename,
            function: errorFrame?.function,
            lineno: errorFrame?.lineno,
            in_app: errorFrame?.in_app,
            isFromNodeModules: errorFrame ? isFrameFromNodeModules(errorFrame) : false,
            isFromThirdPartyScript: errorFrame ? isFrameFromThirdPartyScript(errorFrame) : false,
        },
        nodeModulesFrames: nodeModulesFrames.map((frame) => ({
            filename: frame.filename,
            function: frame.function,
            lineno: frame.lineno,
        })),
        thirdPartyFrames: thirdPartyFrames.map((frame) => ({
            filename: frame.filename,
            function: frame.function,
            lineno: frame.lineno,
        })),
        totalFrames: frames.length,
        nodeModulesFrameCount: nodeModulesFrames.length,
        thirdPartyFrameCount: thirdPartyFrames.length,
    };
};

const initSentry = async (
    isDev: boolean,
    environment: string,
    sentryDSN: string
): Promise<void> => {
    if (isDev) {
        logger.debug(
            `Not initialising Sentry as you're currently running within a DEV environment.`
        );
        return;
    }

    logger.debug(`Initialising Sentry`);

    // Dynamic import - Sentry will be loaded asynchronously and not bundled into vendors-shell
    const Sentry = await import(/* webpackChunkName: "sentry" */ '@sentry/react');
    SentryInstance = Sentry;

    Sentry.init({
        dsn: sentryDSN,
        environment: `${environment}`,
        // Reduce click-time overhead for INP: no DOM click/keypress breadcrumbs, no auto interaction transactions.
        integrations: [
            Sentry.breadcrumbsIntegration({
                dom: false,
                console: true,
            }),
            Sentry.browserTracingIntegration({
                instrumentNavigation: true,
                instrumentPageLoad: true,
                enableInp: true,
                _experiments: {
                    enableInteractions: false,
                },
            }),
        ],
        beforeSend: (event, _hint) => {
            try {
                // Get detailed error source information
                const sourceInfo = getErrorSourceInfo(event as SentryEvent);
                if (sourceInfo.causedByThirdPartyScript || sourceInfo.causedByNodeModules) {
                    return null;
                }
            } catch (error) {
                logger.error('init Sentry: Error getting error source information', error);
            }
            coreInteractionMetrics.registerClientSideErrorEvent(window.location.pathname || '');

            return event;
        },
    });

    window.addEventListener('error', (event) => {
        Sentry.captureException(event);
    });
};

/**
 * Capture an exception using Sentry (async-safe)
 * This function can be called even before Sentry is fully loaded
 */
export const captureException = (error: unknown): void => {
    if (SentryInstance) {
        SentryInstance.captureException(error);
    } else {
        // If Sentry is not loaded yet, queue the error and load Sentry
        import(/* webpackChunkName: "sentry" */ '@sentry/react')
            .then((Sentry) => {
                Sentry.captureException(error);
            })
            .catch(() => {
                // Fallback: log to console if Sentry fails to load
                logger.error('Failed to load Sentry for error capture:', String(error));
            });
    }
};

export default initSentry;
